import { useCallback, useEffect, useState } from 'react';
import { T } from '@design/index';
import { patientApi, type AmbulanceTrip, type EmergencyRequestRow } from '../api';

/**
 * Every SOS this patient has raised, and what happened to it.
 *
 * Two different things, kept apart because they are not the same:
 *
 *  • **Emergency ambulance requests** — `POST /ambulance/requests`, the record
 *    the fleet portal works from. What SOS raises now.
 *  • **Earlier emergency reports** — `POST /emergency/request`, which records
 *    and audit-logs what the patient reported and alerts nobody. That is what
 *    SOS used to raise, and mixing the two would read as though a fleet had
 *    seen them.
 *
 * `dispatched` and `status` are the server's own words and are reported as
 * such: a request nobody acted on says so, because assuming help is on the way
 * is the one mistake this screen must not make.
 */

function fmtWhen(v: string): string {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true });
}

function pretty(s?: string | null): string {
  return (s || '').replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());
}

export default function EmergencyHistory() {
  const [rows, setRows] = useState<EmergencyRequestRow[]>([]);
  const [requests, setRequests] = useState<AmbulanceTrip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setRows(await patientApi.emergencyRequests() ?? []);
    } catch (e: any) {
      setError(e?.body?.message || 'Your earlier SOS requests could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // The requests a fleet can actually accept. Degrades to nothing: failing to
  // read them is not a reason to withhold the reports below.
  useEffect(() => {
    let alive = true;
    patientApi.ambulance.trips()
      .then((t) => { if (alive) setRequests((t ?? []).filter((x) => (x.requestType || '').toUpperCase() === 'EMERGENCY')); })
      .catch(() => { if (alive) setRequests([]); });
    return () => { alive = false; };
  }, []);

  if (loading || (!error && rows.length === 0 && requests.length === 0)) return null;

  const shown = showAll ? rows : rows.slice(0, 3);

  return (
    <div style={{
      background: T.white, border: `1px solid ${T.border}`, borderRadius: 16,
      padding: 16, fontFamily: T.font, marginTop: 14,
    }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: T.text }}>Your emergency requests</div>
      <div style={{ fontSize: 11, color: T.textSec, marginTop: 3, lineHeight: 1.5 }}>
        What you raised, and what happened to it. An SOS here reaches the ambulance fleets on
        NeoHealthCard — it does not call 112 or 108 for you.
      </div>

      {/* Requests a fleet can accept. */}
      {requests.length > 0 && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {requests.slice(0, showAll ? requests.length : 3).map((r) => (
            <div key={r.id} style={{ background: T.ice, border: `1px solid ${T.border}`, borderRadius: 12, padding: '10px 12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: T.text }}>
                    {r.bookingNumber || 'Ambulance request'}
                    <span style={{ color: T.textSec, fontWeight: 500 }}> · {pretty(r.ambulanceType)}</span>
                  </div>
                  <div style={{ fontSize: 10.5, color: T.textTer, marginTop: 2 }}>{fmtWhen(r.createdAt)}</div>
                </div>
                <span style={{
                  fontSize: 9.5, fontWeight: 700, whiteSpace: 'nowrap', borderRadius: 8, padding: '3px 8px',
                  color: T.white, background: r.assignment ? T.success : T.textSec,
                }}>
                  {r.assignment ? 'Crew assigned' : `${pretty(r.status) || 'Requested'} · no crew yet`}
                </span>
              </div>
              {(r.pickup?.address || r.destination?.address) && (
                <div style={{ fontSize: 10.5, color: T.textTer, marginTop: 4 }}>
                  {[r.pickup?.address ? `From: ${r.pickup.address}` : null, r.destination?.address ? `To: ${r.destination.address}` : null].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Reports from before SOS could reach a fleet. */}
      {rows.length > 0 && (
        <div style={{ fontSize: 11.5, fontWeight: 700, color: T.textSec, marginTop: requests.length > 0 ? 16 : 12 }}>
          Earlier emergency reports
        </div>
      )}
      {rows.length > 0 && (
        <div style={{ fontSize: 10.5, color: T.textTer, marginTop: 2, lineHeight: 1.5 }}>
          These were recorded and audit-logged, but no fleet was alerted for them. An ambulance
          you request now goes to the fleets directly.
        </div>
      )}

      {error && <div style={{ fontSize: 11.5, color: T.danger, marginTop: 10 }}>{error}</div>}

      <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {shown.map((r) => (
          <div
            key={r.id}
            style={{
              background: T.ice, border: `1px solid ${T.border}`, borderRadius: 12, padding: '10px 12px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: T.text }}>
                  {pretty(r.emergencyType)}
                  {r.severity && <span style={{ color: T.textSec, fontWeight: 500 }}> · {pretty(r.severity)}</span>}
                </div>
                <div style={{ fontSize: 10.5, color: T.textTer, marginTop: 2 }}>{fmtWhen(r.createdAt)}</div>
              </div>
              <span style={{
                fontSize: 9.5, fontWeight: 700, whiteSpace: 'nowrap', borderRadius: 8, padding: '3px 8px',
                color: T.white, background: r.dispatched ? T.success : T.textSec,
              }}>
                {/* "Recorded" is the server's word for logged-but-not-acted-on;
                    spell that out rather than leaving it to be read as progress. */}
                {r.dispatched ? 'Crew dispatched' : `${pretty(r.status) || 'Recorded'} · no crew sent`}
              </span>
            </div>

            {r.notes && (
              <div style={{ fontSize: 11, color: T.textSec, marginTop: 6, lineHeight: 1.5 }}>{r.notes}</div>
            )}

            {r.location?.address && (
              <div style={{ fontSize: 10.5, color: T.textTer, marginTop: 4 }}>{r.location.address}</div>
            )}
          </div>
        ))}
      </div>

      {rows.length > 3 && (
        <button
          onClick={() => setShowAll((v) => !v)}
          style={{
            marginTop: 10, background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 11.5, fontWeight: 700, color: T.teal, fontFamily: T.font, padding: 0,
          }}
        >
          {showAll ? 'Show fewer' : `Show all ${rows.length}`}
        </button>
      )}
    </div>
  );
}
